import {useState} from 'react';
import {Button} from '@mui/material';
import {MRT_ColumnDef} from 'material-react-table';

import {Pool, useGetPools} from '@entities/pool';
import {CurrencyValue, MrTable} from '@shared/components';
import {DATETIME_FORMAT, entityTableCommonProps} from '@shared/constants';
import {useUrlState} from '@shared/hooks';
import {formatInTz} from '@shared/utils';
import {PoolDetailsDialog} from './PoolDetailsDialog';

import {PaginationState} from '@/shared/types';

const columns: MRT_ColumnDef<Pool>[] = [
  {accessorKey: 'groupId', header: 'Pool ID', size: 220},
  {accessorKey: 'topicId', header: 'Topic', size: 150},
  {accessorKey: 'status', header: 'Status', size: 100},
  {
    accessorKey: 'created',
    header: 'Created',
    Cell: ({row}) => formatInTz(row.original.created, DATETIME_FORMAT),
    size: 160,
  },
  {
    accessorKey: 'real',
    header: 'Real',
    Cell: ({row}) => <CurrencyValue value={row.original.real} />,
    size: 110,
  },
  {
    accessorKey: 'promo',
    header: 'Promo',
    Cell: ({row}) => <CurrencyValue value={row.original.promo} />,
    size: 110,
  },
  {accessorKey: 'participants', header: 'Participants', size: 120},
  {
    accessorKey: 'payable',
    header: 'Payable',
    Cell: ({row}) =>
      row.original.payable == null ? (
        '—'
      ) : (
        <CurrencyValue value={row.original.payable} />
      ),
    size: 110,
  },
  {
    accessorKey: 'winnerTotal',
    header: 'Winner total',
    Cell: ({row}) =>
      row.original.winnerTotal == null ? (
        '—'
      ) : (
        <CurrencyValue value={row.original.winnerTotal} />
      ),
    size: 120,
  },
];

export const PoolsTable = ({
  kind,
  id,
}: {
  kind: 'user' | 'topic';
  id: string;
}) => {
  const [selectedPool, setSelectedPool] = useState<Pool | null>(null);
  const {urlState: pagination, setUrl: setPagination} =
    useUrlState<PaginationState>({
      pageIndex: 0,
      pageSize: 10,
    });
  const {data, isPending, error} = useGetPools(
    kind,
    id,
    {page: pagination.pageIndex + 1, pageSize: pagination.pageSize},
    {enabled: Boolean(id)},
  );
  return (
    <>
      <MrTable
        columns={columns}
        data={data?.items ?? []}
        error={error}
        enableColumnOrdering
        enableHiding
        localStorageKeyForSettings={`${kind}PoolsTableV1`}
        enablePagination
        manualPagination
        enableSorting={false}
        onPaginationChange={setPagination}
        rowCount={data?.total ?? 0}
        state={{isLoading: isPending, pagination}}
        {...entityTableCommonProps}
        enableRowActions
        renderRowActions={({row}) => (
          <Button onClick={() => setSelectedPool(row.original)}>Details</Button>
        )}
      />
      {selectedPool && (
        <PoolDetailsDialog
          pool={selectedPool}
          kind={kind}
          onClose={() => setSelectedPool(null)}
        />
      )}
    </>
  );
};
